import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { createMoodCard, joinChallenge } from "../lib/api";
import { addToHistory } from "../lib/history";

/**
 * Three-beat poetic cascade. Each line fades in ~700ms after the previous
 * and STAYS visible — feels like a slow ritual, not a spinner. Tense moves
 * present → present → future tense so it reads as a small story.
 */
const BEATS = [
    "listening to what you said.",
    "shaping it into something to keep.",
    "almost ready.",
];

export default function Loading() {
    const location = useLocation();
    const navigate = useNavigate();
    const feeling = location.state?.feeling;
    const personalize = location.state?.personalize || null;
    const useAI = location.state?.use_ai !== false;
    const refCode = location.state?.ref_code || null;
    const replyToSlug = location.state?.reply_to_slug || null;
    const packChoice = location.state?.pack || null; // Style Pack v2 — null = auto
    // iter-72 (e): if user came in from a Challenge "write your version" CTA,
    // tag the resulting moodcard with the challenge slug right after creation.
    const challengeSlug = location.state?.challenge_slug || null;
    const [visibleBeats, setVisibleBeats] = useState(0);

    useEffect(() => {
        if (!feeling) {
            navigate("/", { replace: true });
            return;
        }
        // Cascade reveal — 0 → 1 → 2 → 3 lines visible at ~800ms intervals.
        const timers = BEATS.map((_, i) =>
            setTimeout(() => setVisibleBeats(i + 1), 350 + i * 850)
        );

        let cancelled = false;
        const start = Date.now();
        (async () => {
            try {
                const data = await createMoodCard(feeling, packChoice || "cinematic", personalize, useAI, refCode, replyToSlug);
                addToHistory(data);
                // iter-93 — refresh quota chip after a successful pour
                try { window.dispatchEvent(new CustomEvent("quota-changed")); } catch { /* ignore */ }
                // iter-72 (e): fire-and-forget challenge tagging.
                if (challengeSlug) {
                    joinChallenge(data.id, challengeSlug).catch(() => { /* silent — non-blocking */ });
                }
                const elapsed = Date.now() - start;
                // Hold long enough to let all 3 beats land before transition.
                const minDelay = 3200;
                const wait = Math.max(0, minDelay - elapsed);
                setTimeout(() => {
                    if (cancelled) return;
                    // iter-75: skip the Results page entirely — go straight to
                    // CardDetail (preview). All iterations (tone, style, share,
                    // download) happen there now. Default to the `relatable`
                    // variant since that's the highest-share style and matches
                    // what most users expect to see first.
                    const firstCard =
                        Array.isArray(data?.cards) && data.cards.length > 0
                            ? data.cards.find((c) => c.style === "relatable") || data.cards[0]
                            : null;
                    const style = firstCard?.style || "relatable";
                    navigate(`/card/${data.id}/${style}`, {
                        replace: true,
                        state: { mood: data },
                    });
                }, wait);
            } catch (e) {
                console.error("createMoodCard failed:", e);
                const status = e?.response?.status;
                const detail = e?.response?.data?.detail;
                let msg;
                if (status === 429) {
                    // iter-93/94 — structured exhaustion. Navigate to the
                    // dedicated /exhausted screen which offers the carrot
                    // (share-an-existing-card → +1 pour tomorrow) and
                    // the upgrade path. Pass the headline + advice copy
                    // through via router state so the screen reads as
                    // continuous, not a hard error.
                    if (detail && typeof detail === "object" && detail.error === "daily_quota_exhausted") {
                        try {
                            window.dispatchEvent(new CustomEvent("quota-changed"));
                        } catch { /* ignore */ }
                        if (!cancelled) {
                            navigate("/exhausted", {
                                replace: true,
                                state: {
                                    headline: detail.headline,
                                    advice: detail.advice,
                                    restore_feeling: feeling,
                                },
                            });
                        }
                        return;
                    }
                    msg = (typeof detail === "string" && detail) || "Daily limit reached — try again tomorrow.";
                } else if (status === 401 || status === 403) {
                    msg = "Sign in to keep generating cards.";
                } else if (status >= 500 || !status) {
                    msg = "Our card writer hiccupped — try again in a moment.";
                } else {
                    msg = (typeof detail === "string" && detail) || "Something went sideways generating that one.";
                }
                if (!cancelled) {
                    toast.error(msg);
                    navigate("/", { replace: true, state: { restore_feeling: feeling } });
                }
            }
        })();

        return () => {
            cancelled = true;
            timers.forEach(clearTimeout);
        };
    }, [feeling, personalize, useAI, refCode, replyToSlug, packChoice, challengeSlug, navigate]);

    return (
        <div className="relative min-h-screen w-full aurora-wash overflow-hidden flex items-center justify-center" data-testid="loading-screen">
            <div className="absolute inset-0 grain pointer-events-none" />

            <motion.div
                animate={{ scale: [1, 1.25, 1], opacity: [0.20, 0.45, 0.20] }}
                transition={{ duration: 3.8, repeat: Infinity, ease: "easeInOut" }}
                className="absolute h-[460px] w-[460px] rounded-full blur-3xl"
                style={{
                    background:
                        "radial-gradient(circle, rgba(159,231,217,0.30), rgba(242,180,155,0.12) 50%, transparent 75%)",
                }}
            />

            <div className="relative z-10 px-6 text-center max-w-md mx-auto">
                {/* Feeling echo — printed FIRST so the user always sees the words
                    that started this moment. Trims any over-long input to ~120 chars
                    so the cascade below stays vertically balanced. */}
                {feeling && (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="mb-10"
                    >
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <span className="h-px w-8 bg-app-3 opacity-40" aria-hidden="true" />
                            <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3">
                                from you
                            </span>
                            <span className="h-px w-8 bg-app-3 opacity-40" aria-hidden="true" />
                        </div>
                        <motion.p
                            data-testid="loading-feeling-echo"
                            className="font-serif italic text-app text-2xl sm:text-3xl leading-snug max-w-[28ch] mx-auto"
                            animate={{ opacity: [0.85, 1, 0.85] }}
                            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                        >
                            "{feeling.length > 120 ? feeling.slice(0, 120) + "…" : feeling}"
                        </motion.p>
                        <div className="flex items-center justify-center gap-2 mt-5 opacity-60">
                            <span className="h-px w-6 bg-amber-200/40" aria-hidden="true" />
                            <span className="text-amber-200/55 text-[10px]" aria-hidden="true">✦</span>
                            <span className="h-px w-6 bg-amber-200/40" aria-hidden="true" />
                        </div>
                    </motion.div>
                )}

                {/* Pulse-dot — single sign of life */}
                <div className="flex items-center justify-center gap-2 mb-7">
                    <span className="h-1.5 w-1.5 rounded-full pulse-soft" style={{ backgroundColor: "var(--app-text)" }} />
                    <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-2">
                        composing
                    </span>
                </div>

                {/* Three-beat cascade. Lines appear and STAY, building like a
                    small poem. Last line ('almost ready.') reaches just before
                    we navigate, so the transition feels earned. */}
                <div className="flex flex-col items-center gap-3" data-testid="loading-cascade">
                    <AnimatePresence>
                        {BEATS.slice(0, visibleBeats).map((line, i) => (
                            <motion.p
                                key={i}
                                initial={{ opacity: 0, y: 10, filter: "blur(6px)" }}
                                animate={{ opacity: i === visibleBeats - 1 ? 0.95 : 0.6, y: 0, filter: "blur(0px)" }}
                                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                                className="font-serif italic text-app text-xl sm:text-2xl leading-snug"
                                data-testid={`loading-beat-${i}`}
                            >
                                {line}
                            </motion.p>
                        ))}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}
